/**
 * SessionDetail — the /history/:id screen (F4.T6, RF12).
 *
 * Loads GET /sessions/{id}: shows the full transcript segment-by-segment
 * (source + translated), plays the compiled audio artifact (kind
 * 'audio_compiled') in an <audio> element when present, and offers export
 * downloads. Text formats (TXT/SRT/VTT/JSON) are always available; MP3/WAV are
 * shown only when a compiled-audio artifact exists.
 *
 * Exports are plain navigations to the export URL so the backend's
 * Content-Disposition drives the download.
 */

import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  historyApi,
  type ExportFormat,
  type SessionDetail as SessionDetailData,
} from '@/features/history/historyApi';

const TEXT_EXPORTS: ReadonlyArray<{ format: ExportFormat; label: string }> = [
  { format: 'txt', label: 'TXT' },
  { format: 'srt', label: 'SRT' },
  { format: 'vtt', label: 'VTT' },
  { format: 'json', label: 'JSON' },
];

const AUDIO_EXPORTS: ReadonlyArray<{ format: ExportFormat; label: string }> = [
  { format: 'mp3', label: 'MP3' },
  { format: 'wav', label: 'WAV' },
];

const AUDIO_KIND = 'audio_compiled';

function formatTimestamp(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function SessionDetail(): ReactElement {
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<SessionDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await historyApi.detail(id));
    } catch {
      setError('Não foi possível carregar a sessão.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const audioArtifact = useMemo(
    () => detail?.artifacts.find((a) => a.kind === AUDIO_KIND) ?? null,
    [detail],
  );

  function download(format: ExportFormat): void {
    if (!id) return;
    // Navigate to the export URL; Content-Disposition triggers the download.
    window.open(historyApi.exportUrl(id, format), '_blank', 'noopener');
  }

  return (
    <main style={styles.page}>
      <nav style={styles.nav}>
        <Link to="/history" style={styles.link}>
          ← Histórico
        </Link>
        <Link to="/" style={styles.link}>
          Console
        </Link>
      </nav>

      <section style={styles.body}>
        {loading ? (
          <p style={styles.muted}>Carregando…</p>
        ) : error ? (
          <p style={styles.error}>{error}</p>
        ) : !detail ? (
          <p style={styles.muted}>Sessão não encontrada.</p>
        ) : (
          <>
            <header style={styles.header}>
              <h1 style={styles.title}>{detail.name}</h1>
              <div style={styles.metaLine}>
                {(detail.source_lang_mode === 'auto'
                  ? 'auto'
                  : (detail.source_lang ?? '—'))}{' '}
                → {detail.target_lang} ·{' '}
                {new Date(detail.created_at).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </div>
            </header>

            {audioArtifact && (
              <div style={styles.audioBlock}>
                <h2 style={styles.sectionTitle}>Áudio compilado</h2>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio
                  controls
                  preload="metadata"
                  src={audioArtifact.url}
                  style={styles.audio}
                >
                  Seu navegador não suporta reprodução de áudio.
                </audio>
              </div>
            )}

            <div style={styles.exportBar}>
              <span style={styles.exportLabel}>Exportar:</span>
              {TEXT_EXPORTS.map((e) => (
                <button
                  key={e.format}
                  type="button"
                  style={styles.exportButton}
                  onClick={() => download(e.format)}
                >
                  {e.label}
                </button>
              ))}
              {audioArtifact &&
                AUDIO_EXPORTS.map((e) => (
                  <button
                    key={e.format}
                    type="button"
                    style={styles.exportButton}
                    onClick={() => download(e.format)}
                  >
                    {e.label}
                  </button>
                ))}
            </div>

            <h2 style={styles.sectionTitle}>Transcrição</h2>
            {detail.segments.length === 0 ? (
              <p style={styles.muted}>
                {detail.full_text || 'Sem transcrição disponível.'}
              </p>
            ) : (
              <ol style={styles.segments}>
                {detail.segments.map((seg) => (
                  <li key={seg.segment_id} style={styles.segment}>
                    <span style={styles.ts}>{formatTimestamp(seg.ts_ms)}</span>
                    <div style={styles.segText}>
                      <div style={styles.src}>{seg.src}</div>
                      <div style={styles.translated}>{seg.translated}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </section>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'var(--rt-bg)', color: 'var(--rt-text)' },
  nav: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    padding: '0.75rem 1.5rem',
    borderBottom: '1px solid var(--rt-border)',
    background: 'var(--rt-surface)',
  },
  link: { color: 'var(--rt-primary)', textDecoration: 'none', fontSize: '0.9rem' },
  body: {
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    maxWidth: 860,
    margin: '0 auto',
  },
  header: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  title: { margin: 0, fontSize: '1.4rem' },
  metaLine: { fontSize: '0.85rem', color: 'var(--rt-text-muted)' },
  sectionTitle: { margin: '0.5rem 0 0', fontSize: '1.05rem' },
  audioBlock: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  audio: { width: '100%' },
  exportBar: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderTop: '1px solid var(--rt-border)',
    borderBottom: '1px solid var(--rt-border)',
  },
  exportLabel: { fontSize: '0.85rem', color: 'var(--rt-text-muted)', marginRight: '0.25rem' },
  exportButton: {
    padding: '0.4rem 0.8rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-surface)',
    color: 'var(--rt-text)',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  segments: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  segment: { display: 'flex', gap: '0.75rem', alignItems: 'flex-start' },
  ts: {
    fontSize: '0.75rem',
    color: 'var(--rt-text-muted)',
    fontVariantNumeric: 'tabular-nums',
    paddingTop: '0.15rem',
    minWidth: 52,
  },
  segText: { display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1, minWidth: 0 },
  src: { fontSize: '0.85rem', color: 'var(--rt-text-muted)' },
  translated: { fontSize: '0.95rem', color: 'var(--rt-text)' },
  muted: { color: 'var(--rt-text-muted)' },
  error: { color: 'var(--rt-danger)', fontSize: '0.85rem', margin: 0 },
};
